/* =========================================================================
   RUGKARI — global.js
   One small dependency-free script: drawers, sticky header, reveal
   animations, variant picker, AJAX cart, hover prefetch.
   ========================================================================= */
(function () {
  'use strict';

  var configEl = document.getElementById('rk-config');
  var RK = configEl ? JSON.parse(configEl.textContent) : { routes: {}, strings: {} };

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ----------------------------------------------------------------------
     Drawers (mobile menu, cart drawer)
     Openers:  [data-drawer-open="<name>"]
     Drawer:   [data-drawer="<name>"]
     Closers:  [data-drawer-close] inside the drawer (incl. overlay)
     ---------------------------------------------------------------------- */
  var openDrawer = null;

  function setDrawer(name, open) {
    var drawer = qs('[data-drawer="' + name + '"]');
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    document.body.style.overflow = open ? 'hidden' : '';
    openDrawer = open ? name : null;
    if (open) {
      var focusable = qs('button, a, input, [tabindex]', drawer);
      if (focusable) focusable.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      setDrawer(opener.getAttribute('data-drawer-open'), true);
      return;
    }
    var closer = e.target.closest('[data-drawer-close]');
    if (closer && openDrawer) {
      e.preventDefault();
      setDrawer(openDrawer, false);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openDrawer) setDrawer(openDrawer, false);
  });

  /* Search bar toggle */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-search-toggle]');
    if (!toggle) return;
    var bar = qs('#SearchBar');
    if (!bar) return;
    var isOpen = bar.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', isOpen);
    if (isOpen) { var input = qs('input[type="search"]', bar); if (input) input.focus(); }
  });

  /* ----------------------------------------------------------------------
     Sticky header: hide on scroll down, show on scroll up
     ---------------------------------------------------------------------- */
  var headerWrapper = qs('.header-wrapper');
  if (headerWrapper) {
    var annSection = qs('.shopify-section-announcement');

    function measureHeader() {
      document.documentElement.style.setProperty('--header-height', headerWrapper.offsetHeight + 'px');
      document.documentElement.style.setProperty('--announcement-height', (annSection ? annSection.offsetHeight : 0) + 'px');
    }
    measureHeader();
    /* Re-measure once fonts and assets settle so offsets are exact */
    window.addEventListener('load', measureHeader);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureHeader);
    window.addEventListener('resize', measureHeader);

    headerWrapper.classList.toggle('at-top', window.scrollY < 24);

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        headerWrapper.classList.toggle('at-top', window.scrollY < 24);
        ticking = false;
      });
    }, { passive: true });
  }

  /* ----------------------------------------------------------------------
     Reveal-on-scroll. Elements opt in with [data-reveal];
     without JS they simply stay visible.
     ---------------------------------------------------------------------- */
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });

    qsa('[data-reveal]').forEach(function (el, i) {
      el.classList.add('reveal');
      el.style.transitionDelay = Math.min(i % 8, 5) * 60 + 'ms';
      io.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     Quantity steppers
     ---------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.quantity__button');
    if (!btn) return;
    var input = qs('.quantity__input', btn.closest('.quantity'));
    if (!input) return;
    var value = parseInt(input.value, 10) || 0;
    var min = parseInt(input.min, 10) || 0;
    value += btn.getAttribute('data-dir') === 'up' ? 1 : -1;
    if (value < min) value = min;
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ----------------------------------------------------------------------
     Cart helpers
     ---------------------------------------------------------------------- */
  function updateCartCount(count) {
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.setAttribute('data-count', count);
    });
  }

  function refreshCartDrawer(openAfter) {
    var drawer = qs('#CartDrawer');
    if (!drawer) {
      if (openAfter) window.location.href = RK.routes.cart;
      return Promise.resolve();
    }
    drawer.classList.add('is-loading');
    return fetch(window.location.pathname + '?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = document.createElement('div');
        html.innerHTML = sections['cart-drawer'];
        var fresh = qs('#CartDrawer', html);
        if (fresh) {
          var wasOpen = drawer.classList.contains('is-open');
          drawer.innerHTML = fresh.innerHTML;
          drawer.classList.toggle('is-open', wasOpen || !!openAfter);
          if (wasOpen || openAfter) document.body.style.overflow = 'hidden';
          if (openAfter) openDrawer = 'cart';
        }
      })
      .finally(function () { drawer.classList.remove('is-loading'); });
  }

  function fetchCartCount() {
    return fetch(RK.routes.cart + '.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCartCount(cart.item_count); return cart; });
  }

  /* Add to cart (any form[data-product-form]) */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-product-form]');
    if (!form || RK.cartType !== 'drawer') return;
    e.preventDefault();

    var button = qs('[type="submit"]', form);
    var label = qs('span', button);
    var original = label ? label.textContent : '';
    if (button) button.setAttribute('aria-disabled', 'true');
    if (label) label.textContent = RK.strings.adding;

    var body, headers = { 'X-Requested-With': 'XMLHttpRequest' };
    var addon = getAddonItem(form);
    if (addon) {
      var idEl = qs('input[name="id"]', form);
      var qtyEl = qs('input[name="quantity"]', form);
      var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
      var rugId = idEl ? idEl.value : null;
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify({
        items: [
          { id: rugId, quantity: qty, properties: { 'Stain Resistant Coating': 'Added' } },
          { id: addon.id, quantity: qty, properties: { '_addon_for': rugId } }
        ]
      });
    } else {
      body = new FormData(form);
    }
    fetch(RK.routes.cartAdd + '.js', {
      method: 'POST',
      headers: headers,
      body: body
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error(res.data.description || RK.strings.cartError);
        if (label) label.textContent = RK.strings.added;
        return Promise.all([fetchCartCount(), refreshCartDrawer(true)]);
      })
      .catch(function (err) {
        var status = qs('[data-form-status]', form.closest('section') || document);
        if (status) {
          status.hidden = false;
          status.textContent = err.message;
        } else {
          window.alert(err.message);
        }
      })
      .finally(function () {
        if (button) button.removeAttribute('aria-disabled');
        window.setTimeout(function () { if (label) label.textContent = original; }, 1500);
      });
  });

  /* Line-item quantity change / removal.
     Works in the drawer and on the cart page via [data-line-quantity]
     inputs and [data-line-remove] buttons. */
  function changeLine(line, quantity, scopeEl) {
    var sectionId = scopeEl ? scopeEl.getAttribute('data-section-id') : null;
    var payload = { line: line, quantity: quantity };
    if (sectionId && sectionId !== 'cart-drawer') payload.sections = sectionId;

    return fetch(RK.routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        if (payload.sections && cart.sections && cart.sections[sectionId]) {
          var holder = document.createElement('div');
          holder.innerHTML = cart.sections[sectionId];
          var freshInner = qs('[data-cart-root]', holder);
          var target = qs('[data-cart-root]', scopeEl);
          if (freshInner && target) target.innerHTML = freshInner.innerHTML;
        }
        return refreshCartDrawer(false);
      });
  }

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-line-quantity]');
    if (!input) return;
    var scope = input.closest('[data-section-id]');
    changeLine(parseInt(input.getAttribute('data-line'), 10), parseInt(input.value, 10) || 0, scope);
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-line-remove]');
    if (!btn) return;
    e.preventDefault();
    var scope = btn.closest('[data-section-id]');
    changeLine(parseInt(btn.getAttribute('data-line'), 10), 0, scope);
  });

  /* Cart note */
  document.addEventListener('change', function (e) {
    var note = e.target.closest('[data-cart-note]');
    if (!note) return;
    fetch(RK.routes.cartUpdate + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note.value })
    });
  });

  /* ----------------------------------------------------------------------
     Variant picker
     Root: [data-variant-picker] containing a JSON script of variants,
     option inputs, and living next to the product form + price.
     ---------------------------------------------------------------------- */
  qsa('[data-variant-picker]').forEach(function (picker) {
    var jsonEl = qs('script[type="application/json"]', picker);
    if (!jsonEl) return;
    var variants = JSON.parse(jsonEl.textContent);
    var sectionEl = picker.closest('section') || document;
    var form = qs('form[data-product-form]', sectionEl) || qs('form[action*="/cart/add"]', sectionEl);
    var idInput = form ? qs('input[name="id"]', form) : null;
    var priceEl = qs('[data-product-price]', sectionEl);
    var button = form ? qs('[type="submit"]', form) : null;
    var buttonLabel = button ? qs('span', button) : null;

    function selectedOptions() {
      return qsa('fieldset', picker).map(function (set) {
        var checked = qs('input:checked', set);
        if (checked) return checked.value;
        var select = qs('select', set);
        return select ? select.value : null;
      });
    }

    function findVariant(options) {
      return variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === options[i]; });
      });
    }

    picker.addEventListener('change', function () {
      var variant = findVariant(selectedOptions());

      qsa('[data-option-value]', picker).forEach(function (label) {
        var set = label.closest('fieldset');
        var input = qs('input:checked', set);
        if (input && label.getAttribute('data-option-index') === input.getAttribute('data-option-index')) {
          var valueEl = qs('legend span', set);
          if (valueEl) valueEl.textContent = input.value;
        }
      });

      var stickyBar = qs('.sticky-atc');
      var stickyBtn = stickyBar ? qs('button', stickyBar) : null;
      var stickyLabel = stickyBtn ? qs('span', stickyBtn) : null;

      if (!variant) {
        if (buttonLabel) buttonLabel.textContent = RK.strings.unavailable;
        if (button) button.setAttribute('disabled', '');
        if (stickyBtn) {
          stickyBtn.setAttribute('disabled', '');
          if (stickyLabel) stickyLabel.textContent = RK.strings.unavailable;
        }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (priceEl) {
        var priceHtml = '<span class="price__amount">' + variant.price_formatted + '</span>';
        if (variant.compare_at_price_formatted) {
          priceHtml = '<span class="price__amount">' + variant.price_formatted + '</span>' +
            '<s class="price__compare">' + variant.compare_at_price_formatted + '</s>';
        }
        priceEl.innerHTML = priceHtml;
        priceEl.classList.toggle('price--on-sale', !!variant.compare_at_price_formatted);
      }

      if (button) {
        if (variant.available) {
          button.removeAttribute('disabled');
          if (buttonLabel) buttonLabel.textContent = RK.strings.addToCart;
        } else {
          button.setAttribute('disabled', '');
          if (buttonLabel) buttonLabel.textContent = RK.strings.soldOut;
        }
      }

      if (stickyBtn) {
        if (variant.available) {
          stickyBtn.removeAttribute('disabled');
          if (stickyLabel) stickyLabel.textContent = RK.strings.addToCart;
        } else {
          stickyBtn.setAttribute('disabled', '');
          if (stickyLabel) stickyLabel.textContent = RK.strings.soldOut;
        }
      }
      var stickyPrice = stickyBar ? qs('.sticky-atc__price', stickyBar) : null;
      if (stickyPrice) stickyPrice.textContent = variant.price_formatted;

      if (variant.featured_media_position) {
        var mediaList = qs('.product__media-list', sectionEl);
        if (mediaList) {
          var target = mediaList.children[variant.featured_media_position - 1];
          if (target) mediaList.scrollTo({ left: target.offsetLeft - mediaList.offsetLeft, behavior: 'smooth' });
        }
      }

      if (window.history.replaceState && variant.id) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    });
  });

  /* ----------------------------------------------------------------------
     Size-priced add-ons (e.g. Stain Resistant Coating).
     The add-on product's variant titles match the rug size names; each
     variant's price is the add-on price for that size. When checked, the
     matching add-on variant is added to cart alongside the rug.
     ---------------------------------------------------------------------- */
  function addonMap(root) {
    if (root._addonMap) return root._addonMap;
    var el = qs('[data-addon-data]', root);
    try { root._addonMap = el ? JSON.parse(el.textContent) : {}; }
    catch (err) { root._addonMap = {}; }
    return root._addonMap;
  }
  function addonSelected(root) {
    var section = root.closest('section') || document;
    var map = addonMap(root);
    var radios = qsa('[data-variant-picker] input[type="radio"]:checked', section);
    for (var i = 0; i < radios.length; i++) {
      if (map[radios[i].value]) return map[radios[i].value];
    }
    return null;
  }
  function getAddonItem(form) {
    var section = form.closest('section') || document;
    var root = qs('[data-addons]', section);
    if (!root || root.hidden) return null;
    var toggle = qs('[data-addon-toggle]', root);
    if (!toggle || !toggle.checked) return null;
    return addonSelected(root);
  }

  qsa('[data-addons]').forEach(function (root) {
    var section = root.closest('section') || document;
    var priceEl = qs('[data-addon-price]', root);
    var totalEl = qs('[data-addon-total]', root);
    var toggle = qs('[data-addon-toggle]', root);

    var nameEl = qs('.addon__text', root);
    var addonName = nameEl && nameEl.firstChild ? nameEl.firstChild.textContent.trim() : 'the add-on';
    var baseRug = null;

    function render(fromVariant) {
      var sel = addonSelected(root);
      if (!sel) { root.hidden = true; return; }
      root.hidden = false;
      if (priceEl) priceEl.textContent = '(+' + sel.price_formatted + ')';

      var amtEl = qs('[data-product-price] .price__amount', section);
      if (amtEl) {
        // Capture the rug-only price whenever the variant (size) changes
        if (fromVariant || baseRug === null) baseRug = amtEl.textContent;
        var sym = (baseRug.match(/^[^\d]*/) || [''])[0];
        // Strip the leading currency symbol (e.g. "Rs.") FIRST so its dot
        // isn't mistaken for a decimal, then drop thous-separators.
        var rugNum = parseFloat(baseRug.replace(/^\D*/, '').replace(/,/g, '')) || 0;
        if (toggle && toggle.checked) {
          var total = rugNum + (sel.price / 100);
          amtEl.textContent = sym + total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        } else {
          amtEl.textContent = baseRug;
        }
      }

      if (totalEl) {
        if (toggle && toggle.checked) {
          totalEl.hidden = false;
          totalEl.textContent = 'Includes ' + addonName + ' (+' + sel.price_formatted + ')';
        } else {
          totalEl.hidden = true;
        }
      }
    }

    if (toggle) toggle.addEventListener('change', function () { render(false); });
    var picker = qs('[data-variant-picker]', section);
    if (picker) picker.addEventListener('change', function () { window.setTimeout(function () { render(true); }, 0); });
    render(true);
  });

  /* ----------------------------------------------------------------------
     Product recommendations: Shopify's related products, loaded per product
     so each PDP shows different, relevant rugs (not a fixed collection).
     ---------------------------------------------------------------------- */
  qsa('[data-product-recommendations]').forEach(function (el) {
    var url = el.getAttribute('data-url');
    if (!url) return;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.querySelector('[data-product-recommendations]');
        if (fresh) el.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});
  });

  /* ----------------------------------------------------------------------
     Cinematic hero: subtle parallax drift on scroll for depth
     ---------------------------------------------------------------------- */
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
    var heroMedias = qsa('.hero__media');
    if (heroMedias.length) {
      var heroTicking = false;
      var updateHeroParallax = function () {
        var y = window.scrollY || window.pageYOffset || 0;
        var shift = (y * 0.08).toFixed(1);
        for (var i = 0; i < heroMedias.length; i++) {
          heroMedias[i].style.transform = 'translate3d(0,' + shift + 'px,0)';
        }
        heroTicking = false;
      };
      window.addEventListener('scroll', function () {
        if (!heroTicking) { heroTicking = true; window.requestAnimationFrame(updateHeroParallax); }
      }, { passive: true });
      updateHeroParallax();
    }
  }

  /* Product gallery thumbnails */
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-thumb-index]');
    if (!thumb) return;
    var sectionEl = thumb.closest('section') || document;
    var mediaList = qs('.product__media-list', sectionEl);
    if (!mediaList) return;
    var index = parseInt(thumb.getAttribute('data-thumb-index'), 10);
    var target = mediaList.children[index];
    if (target) mediaList.scrollTo({ left: target.offsetLeft - mediaList.offsetLeft, behavior: 'smooth' });
    qsa('[data-thumb-index]', sectionEl).forEach(function (t) { t.classList.remove('is-active'); });
    thumb.classList.add('is-active');
  });

  /* ----------------------------------------------------------------------
     Facets: auto-submit the filter form on change
     ---------------------------------------------------------------------- */
  qsa('form[data-facets-form]').forEach(function (form) {
    form.addEventListener('change', function (e) {
      if (e.target.closest('[data-no-autosubmit]')) return;
      form.submit();
    });
  });

  /* ----------------------------------------------------------------------
     Product gallery: cursor-follow magnifier zoom + fullscreen lightbox
     ---------------------------------------------------------------------- */
  var fineHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  qsa('.product__media-item--image').forEach(function (item) {
    if (fineHover) {
      item.addEventListener('mousemove', function (e) {
        var rect = item.getBoundingClientRect();
        item.style.setProperty('--zoom-x', ((e.clientX - rect.left) / rect.width * 100).toFixed(1) + '%');
        item.style.setProperty('--zoom-y', ((e.clientY - rect.top) / rect.height * 100).toFixed(1) + '%');
      });
      item.addEventListener('mouseenter', function () { item.classList.add('is-zooming'); });
      item.addEventListener('mouseleave', function () { item.classList.remove('is-zooming'); });
    }
    item.addEventListener('click', function () {
      var lightbox = qs('.lightbox', item.closest('section') || document);
      if (!lightbox || !lightbox.showModal) return;
      lightbox.showModal();
      var index = parseInt(item.getAttribute('data-media-index'), 10) || 0;
      var track = qs('.lightbox__track', lightbox);
      var slide = track && track.children[index];
      if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'instant' in track ? 'instant' : 'auto' });
    });
  });

  document.addEventListener('click', function (e) {
    var closer = e.target.closest('[data-lightbox-close]');
    if (closer) closer.closest('dialog').close();
    var dialog = e.target.closest('dialog.lightbox');
    if (dialog && e.target === dialog) dialog.close();
  });

  /* ----------------------------------------------------------------------
     Sticky mobile add-to-cart bar: shows when the buy button scrolls away
     ---------------------------------------------------------------------- */
  var stickyBar = qs('.sticky-atc');
  if (stickyBar) {
    var mainForm = qs('form[data-product-form]');

    // Floating buy bar (mobile): appears once the shopper scrolls past the first
    // screen and stays visible whether they scroll up or down.
    var toggleSticky = function () {
      stickyBar.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.6);
    };
    window.addEventListener('scroll', toggleSticky, { passive: true });
    window.addEventListener('resize', toggleSticky, { passive: true });
    toggleSticky();

    var stickyBtn = qs('button', stickyBar);
    if (stickyBtn && mainForm) {
      stickyBtn.addEventListener('click', function () {
        if (mainForm.requestSubmit) mainForm.requestSubmit();
        else mainForm.submit();
      });
    }
  }

  /* ----------------------------------------------------------------------
     Quick add (single-variant products, from collection cards)
     ---------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    btn.classList.add('is-loading');
    fetch(RK.routes.cartAdd + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: parseInt(btn.getAttribute('data-quick-add'), 10), quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error(RK.strings.cartError);
        return Promise.all([fetchCartCount(), refreshCartDrawer(true)]);
      })
      .catch(function () { window.location.href = btn.getAttribute('data-url') || RK.routes.cart; })
      .finally(function () { btn.classList.remove('is-loading'); });
  });

  /* ----------------------------------------------------------------------
     Predictive search: live results as you type
     ---------------------------------------------------------------------- */
  var searchInput = qs('#SearchBar input[type="search"]');
  if (searchInput) {
    var resultsBox = document.createElement('div');
    resultsBox.className = 'predictive-container';
    searchInput.closest('.page-width').appendChild(resultsBox);
    var debounce = null;
    searchInput.addEventListener('input', function () {
      var q = searchInput.value.trim();
      clearTimeout(debounce);
      if (q.length < 2) { resultsBox.innerHTML = ''; return; }
      debounce = setTimeout(function () {
        fetch('/search/suggest?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6&section_id=predictive-search')
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var holder = document.createElement('div');
            holder.innerHTML = html;
            var fresh = qs('#PredictiveResults', holder);
            resultsBox.innerHTML = fresh ? fresh.innerHTML : '';
          })
          .catch(function () {});
      }, 250);
    });
  }

  /* ----------------------------------------------------------------------
     Recently viewed (localStorage, client-rendered)
     ---------------------------------------------------------------------- */
  var rvSection = qs('[data-recently-viewed]');
  if (rvSection) {
    var RV_KEY = 'rk:recently-viewed';
    var list = [];
    try { list = JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (e) {}

    var currentRaw = rvSection.getAttribute('data-current');
    var current = null;
    try { current = currentRaw ? JSON.parse(currentRaw) : null; } catch (e) {}

    var toShow = list.filter(function (item) { return !current || item.h !== current.h; }).slice(0, 4);
    if (toShow.length) {
      var grid = qs('[data-rv-grid]', rvSection);
      toShow.forEach(function (item) {
        var a = document.createElement('a');
        a.className = 'rv-card';
        a.href = item.u;
        a.innerHTML = '<div class="rv-card__media"><img loading="lazy" alt=""></div>' +
          '<span class="rv-card__title"></span><span class="rv-card__price"></span>';
        qs('img', a).src = item.i;
        qs('.rv-card__title', a).textContent = item.t;
        qs('.rv-card__price', a).textContent = item.p;
        grid.appendChild(a);
      });
      rvSection.hidden = false;
    }

    if (current && current.h) {
      var next = [current].concat(list.filter(function (item) { return item.h !== current.h; })).slice(0, 8);
      try { localStorage.setItem(RV_KEY, JSON.stringify(next)); } catch (e) {}
    }
  }

  /* ----------------------------------------------------------------------
     Rotating announcement messages
     ---------------------------------------------------------------------- */
  qsa('[data-announcement]').forEach(function (bar) {
    var items = qsa('.announcement-bar__item', bar);
    if (items.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var i = 0;
    var speed = parseInt(bar.getAttribute('data-speed'), 10) || 4000;
    setInterval(function () {
      var current = items[i];
      current.classList.add('is-leaving');
      current.classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
      window.setTimeout(function () { current.classList.remove('is-leaving'); }, 600);
    }, speed);
  });

  /* ----------------------------------------------------------------------
     Dismissible announcement bar
     ---------------------------------------------------------------------- */
  var ANN_KEY = 'rk:announcement-dismissed';
  try {
    if (sessionStorage.getItem(ANN_KEY)) document.body.classList.add('announcement-dismissed');
  } catch (e) {}
  document.addEventListener('click', function (e) {
    var close = e.target.closest('[data-announcement-close]');
    if (!close) return;
    document.body.classList.add('announcement-dismissed');
    try { sessionStorage.setItem(ANN_KEY, '1'); } catch (err) {}
    var hw = qs('.header-wrapper');
    if (hw) document.documentElement.style.setProperty('--announcement-height', '0px');
  });

  /* ----------------------------------------------------------------------
     Hover / touch prefetch: warm the cache for the next navigation
     ---------------------------------------------------------------------- */
  var prefetched = new Set();
  var saveData = navigator.connection && navigator.connection.saveData;

  function prefetch(e) {
    if (saveData) return;
    var link = e.target.closest('a[href]');
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href || href.charAt(0) !== '/' || href.indexOf('//') === 0) return;
    if (/\/(cart|checkout|account)/.test(href) || prefetched.has(href)) return;
    prefetched.add(href);
    var tag = document.createElement('link');
    tag.rel = 'prefetch';
    tag.href = href;
    document.head.appendChild(tag);
  }

  document.addEventListener('mouseover', prefetch, { passive: true });
  document.addEventListener('touchstart', prefetch, { passive: true });

  /* ----------------------------------------------------------------------
     Hero slideshow: crossfade autoplay with dots, pause on hover
     ---------------------------------------------------------------------- */
  qsa('[data-hero-slider]').forEach(function (slider) {
    var slides = qsa('.hero-slide', slider);
    if (slides.length < 2) return;
    var dots = qsa('.hero-dots__dot', slider);
    var index = 0;
    var timer = null;
    var speed = (parseInt(slider.getAttribute('data-speed'), 10) || 6) * 1000;
    var auto = slider.getAttribute('data-autoplay') === 'true' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function go(n) {
      slides[index].classList.remove('is-active');
      if (dots[index]) dots[index].classList.remove('is-active');
      index = (n + slides.length) % slides.length;
      slides[index].classList.add('is-active');
      if (dots[index]) dots[index].classList.add('is-active');
    }
    function start() { if (auto && !timer) timer = setInterval(function () { go(index + 1); }, speed); }
    function stop() { clearInterval(timer); timer = null; }

    dots.forEach(function (dot, n) {
      dot.addEventListener('click', function () { stop(); go(n); start(); });
    });
    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', start);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });
    start();
  });

  /* SEO read-more label: keyboard support */
  document.addEventListener('keydown', function (e) {
    var label = e.target.closest('.seo-text__label');
    if (!label || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    label.click();
  });

  /* Back to top */
  document.addEventListener('click', function (e) {
    var top = e.target.closest('.footer__back-top');
    if (!top) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
})();
